import type { OverlayStyleOverride } from '../types/persistence'
import { createLocalStore } from './keyedLocalStore'

/**
 * Per-user, PER-TOOL default line style for drawings, keyed by the overlay
 * `name` (e.g. 'horizontalStraightLine', 'rayLine', 'rect'). GLOBAL scope —
 * a single store shared across every chart, mirroring `indicatorUserDefaults`.
 * "Save as default" in the overlay settings drawer writes here; new overlays
 * of that name spawn with the stored style.
 *
 * Intentionally local-only — no network round-trip, no backend schema
 * commitment. Synchronous read/write against localStorage.
 */
/**
 * A saved per-tool default: line style, plus any per-instance state the owning
 * overlay template understands (a Fib's per-level colour/visibility map).
 * `extendData` is opaque here — only the template interprets it.
 */
export interface OverlayUserDefault {
  style?: OverlayStyleOverride
  extendData?: unknown
}

/** Entries written before the widening are a BARE `OverlayStyleOverride`. */
type StoredDefault = OverlayUserDefault | OverlayStyleOverride

const store = createLocalStore<StoredDefault>('edge.chart.overlayDefaults.v1')

/**
 * Read-time migration of legacy bare-style entries. Detected structurally: the
 * new shape always has `style` or `extendData`, and neither is a valid
 * `OverlayStyleOverride` field, so the two can never be confused.
 *
 * Migrating on READ rather than in a one-shot pass means nobody loses a saved
 * default to a refactor, including users who don't open the app until later.
 */
function toUserDefault(stored: StoredDefault | undefined): OverlayUserDefault | undefined {
  if (!stored || typeof stored !== 'object') return undefined
  if ('style' in stored || 'extendData' in stored) return stored as OverlayUserDefault
  return { style: stored as OverlayStyleOverride }
}

export function getOverlayUserDefault(name: string): OverlayUserDefault | undefined {
  return toUserDefault(store.get(name))
}

/**
 * Built-in fallback style for a freshly drawn overlay when the user hasn't saved
 * a per-tool default. Yellow (`#eab308` — the 5th swatch in the color picker's
 * yellow column) reads clearly on both light and dark charts and stands apart
 * from the candle up/down greens and reds.
 */
export const DEFAULT_OVERLAY_STYLE: OverlayStyleOverride = { color: '#eab308' }

/**
 * Tools that carry their own intrinsic colors (position handles are semantically
 * green/red; the measure/ruler tool tints by direction) and therefore must NOT
 * inherit the built-in default drawing color — only an explicit user default.
 */
// Tools that carry their own palette. The built-in yellow must not paint over
// them — the range tools are blue by design, and the position tool green/red.
const SELF_STYLED_TOOLS = new Set<string>([
  'longPositionTool',
  'shortPositionTool',
  // Green/red by inferred direction, red stop — its own palette, like the
  // position tools. Absent from SELF_STYLED_SWATCH for the same reason.
  'positionPlan',
  'measure',
  'dateRange',
  'priceRange',
  'datePriceRange',
  // Eleven levels, each with its own colour. Deliberately absent from
  // SELF_STYLED_SWATCH below — no single colour represents it, so the quick bar
  // shows the explicit "uses its own colours" state instead of a lie.
  'fibonacciLine',
])

/**
 * Swatch colour for tools that paint themselves rather than taking the global
 * default. Without this the quick bar showed the built-in yellow for a blue
 * range tool — claiming a colour the drawing was not using.
 *
 * Tools with no single representative colour (the position tool is green AND
 * red) are deliberately absent: the swatch then renders an explicit "uses its
 * own colours" state instead of picking one arbitrarily.
 */
const SELF_STYLED_SWATCH: Record<string, string> = {
  dateRange: '#2962ff',
  priceRange: '#2962ff',
  datePriceRange: '#2962ff',
}

/** Representative colour for a self-styled tool, or undefined if it has none. */
export function selfStyledSwatch(name: string): string | undefined {
  return SELF_STYLED_SWATCH[name]
}

/**
 * The EFFECTIVE default style for a new overlay of `name`: a saved per-tool user
 * default layered over the built-in default (user fields win per-field, so a
 * partial saved default still inherits the built-in color/width for fields it
 * omits). Self-styled tools skip the built-in and use only a saved default.
 */
export function resolveOverlayDefault(name: string): OverlayUserDefault | undefined {
  const user = getOverlayUserDefault(name)
  if (SELF_STYLED_TOOLS.has(name)) return user
  return { ...user, style: { ...DEFAULT_OVERLAY_STYLE, ...(user?.style ?? {}) } }
}

export function setOverlayUserDefault(name: string, def: OverlayUserDefault): void {
  store.set(name, def)
}

export function clearOverlayUserDefault(name: string): void {
  store.clear(name)
}
